import React, { useState, useEffect } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import _ from "lodash";
import clsx from "clsx";
import { Card, CardContent, CardHeader, Typography, Box, IconButton, Avatar, Menu, MenuItem, Badge, Tooltip } from "@mui/material";
import { Business, AccountBalance, MoreVert, ExpandMore } from "@mui/icons-material";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDrag, useDrop } from "react-dnd";
import Layout from "../../../layout/default";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { styled } from '@mui/material/styles';

const useStyles = styled((theme) => ({
  root: {
    background: "white",
    display: "inline-block",
    borderRadius: 16
  },
  expand: {
    transform: "rotate(0deg)",
    marginTop: -10,
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.short
    })
  },
  expandOpen: {
    transform: "rotate(180deg)"
  },
  avatar: {
    backgroundColor: "#ECECF4"
  }
}));

function Organization({ org, onCollapse, collapsed }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [{ canDrop, isOver }, drop] = useDrop({
    accept: "parent",
    drop: () => ({ name: org.user_id }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });
  const isActive = canDrop && isOver;
  let backgroundColor = "white";
  if (isActive) {
    backgroundColor = "#ddffd2";
  } else if (canDrop) {
    backgroundColor = "#ffeedc";
  }
  return (
    <Card
      variant="outlined"
      className={classes.root}
      ref={drop}
      style={{ backgroundColor: backgroundColor,  
        display: 'inline-flex'  
       } }
    >
      <CardHeader
        avatar={
          <Tooltip
            title={`${org.children.length} child`}
            arrow
          >
            <Badge
              style={{ cursor: "pointer" }}
              color="secondary"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
              }}
              showZero
              invisible={!collapsed}
              overlap="circle"
              badgeContent={org.children.length}
              onClick={onCollapse}
            >
              <Avatar className={classes.avatar}>
                <Business color="primary" />
              </Avatar>
            </Badge>
          </Tooltip>
        }
        title={`User: ${org.user_id}`} 
        subheader={`Parent ID: ${org.parent_id}, Leg: ${org.leg}, Active: ${org.is_active ? 'Yes' : 'No'}`}
        action={
          <IconButton size="small" onClick={handleClick}>
          </IconButton>
        }
      />
      <IconButton
        size="small"
        onClick={onCollapse}
        className={clsx(classes.expand, {
          [classes.expandOpen]: !collapsed
        })}
      >
        <ExpandMore />
      </IconButton>
    </Card>
  );
}

function Parent({ parent }) {
  const classes = useStyles();
  const [{ isDragging }, drag] = useDrag({
    type: "parent",
    item: { name: parent.user_id },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (item && dropResult) {
        alert(`You moved ${item.name} to ${dropResult.name}`);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });
  const opacity = isDragging ? 0.4 : 1;
  return (
    <Card
      variant="outlined"
      className={classes.root}
      ref={drag}
      style={{ cursor: "pointer", opacity }}
    >
      <CardHeader
        avatar={
          <Avatar className={classes.avatar}>
            <AccountBalance color="secondary" />
          </Avatar>
        }
        title={`User: ${parent.user_id}`} 
        subheader={`Parent ID: ${parent.parent_id}, Leg: ${parent.leg}, Active: ${parent.is_active ? 'Yes' : 'No'}`}
      />
    </Card>
  );
}

function Product({ p }) {
  const classes = useStyles();
  return (
    <Card variant="outlined" className={classes.root}>
      <CardContent>
        <Typography variant="subtitle2">{p}</Typography>
      </CardContent>
    </Card>
  );
}

function Node({ o, parent }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const T = parent
    ? TreeNode
    : (props) => (
        <Tree
          {...props}
          lineWidth={"2px"}
          lineColor={"#bbc"}
          lineBorderRadius={"12px"}
        >
          {props.children}
        </Tree>
      );

  return collapsed ? (
    <T
      label={
        <Organization
          org={o}
          onCollapse={handleCollapse}
          collapsed={collapsed}
        />
      }
    />
  ) : (
    <T
      label={
        <Organization
          org={o}
          onCollapse={handleCollapse}
          collapsed={collapsed}
        />
      }
    >
      {_.map(o.children, (c) => (
        <Node key={c.user_id} o={c} parent={o} />
      ))}
    </T>
  );
}

const theme = createTheme({
  palette: {
    background: "white"
  },
  fontFamily: "Roboto, sans-serif"
});

export default function App() {
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken"); // Get token from localStorage
    if (!token) {
      console.error("No auth token found!");
      return;
    }

    fetch(`${process.env.REACT_APP_HOST}api/usertree`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    })
      .then((response) => response.json())
      .then((data) => setOrganization(data))
      .catch((error) => console.error("Error fetching user tree data:", error));
  }, []);

  return (
    <Layout title="user tree">
      <ThemeProvider theme={theme}>
        <Box bgcolor="background" padding={4} height="80vh" display={"inline-flex"}>
          {organization ? (
            <DndProvider backend={HTML5Backend}>
              <Node o={organization} />
            </DndProvider>
          ) : (
            <Typography>Loading organization data...</Typography>
          )}
        </Box>
      </ThemeProvider>
    </Layout>
  );
}
